import type {
  TvCatalogueHome,
  TvCatalogueItem,
  TvRail,
} from "../../../hooks/useTvCatalogue";
import { demoPoster } from "../tv/demoArt";

/**
 * A library for the design preview (`?scheme=…&screen=vod`) and for tests.
 *
 * The sibling of `guide/demoLineup.ts`, and built on the same principle: a
 * fixture is only worth having if it carries the cases a real property has and
 * a happy-path fixture does not. Reviewing a screen against three perfect rails
 * of four perfect posters tells you nothing about the screen you will ship.
 *
 * So it deliberately carries:
 *
 *  1. a full rail with artwork — the case everybody builds for,
 *  2. a rail whose items have NO artwork, because roughly a third of the real
 *     catalogue has no poster at the size a television asks for, and a design
 *     that only works with pictures is a design that breaks in production,
 *  3. a rail with ONE item, which is where a grid's alignment assumptions show,
 *  4. a long title and a long subtitle, because
 *     [[tv-tight-line-height-clipping]] is a real trap and poster captions are
 *     exactly the shape that triggers it,
 *  5. a rail with `hasMore: true`, so the paging path is exercised in preview
 *     rather than only on a bound television.
 *
 * ⚠️ Preview only. `SchemeRouter` passes it exclusively from `App.tsx`'s
 * `?scheme=` branch, which a bound television never takes — so no real room can
 * be made to show invented content. Same guarantee as `previewContent`.
 */

function item(
  id: string,
  title: string,
  extra: Partial<TvCatalogueItem> = {}
): TvCatalogueItem {
  return {
    assetId: id,
    title,
    kind: "movie",
    // Drawn, not fetched: the path this used to point at does not exist,
    // so every poster in the preview came up blank.
    imageUrl: demoPoster(title),
    ...extra,
  };
}

function rail(
  carouselId: string,
  title: string,
  items: TvCatalogueItem[],
  hasMore = false
): TvRail {
  return { carouselId, title, items, page: 1, hasMore };
}

export function demoCatalogue(): TvCatalogueHome {
  return {
    rails: [
      rail(
        "car-popular",
        "Populärt just nu",
        [
          item("t-1", "Midnattssol", { year: 2024, durationSeconds: 6120 }),
          item("t-2", "Sista färjan", { year: 2023, durationSeconds: 5400 }),
          item("t-3", "Vinterviken", {
            year: 2022,
            kind: "series",
            subtitle: "Säsong 2",
          }),
          item("t-4", "Norrsken", { year: 2025, durationSeconds: 7020 }),
          item("t-5", "Havsbandet", { year: 2021, durationSeconds: 5880 }),
          item("t-6", "Tystnaden", { year: 2020, durationSeconds: 6300 }),
        ],
        // The rail believes there is more. Nothing serves page 2 in preview,
        // so this also exercises the "asked for a page that never lands"
        // path - which on a real uplink is a normal Tuesday.
        true
      ),
      // No artwork anywhere. This rail is the one worth staring at.
      rail("car-nordic", "Nordisk dramatik", [
        item("t-7", "Ödemarken", { imageUrl: undefined, year: 2019 }),
        item("t-8", "Brännvidd", { imageUrl: undefined, year: 2023 }),
        item("t-9", "Kust till kust", { imageUrl: undefined, year: 2024 }),
      ]),
      // One item, a long title and a long subtitle with descenders.
      rail("car-featured", "Månadens film", [
        item(
          "t-10",
          "Berättelsen om ljuset över Ångermanälven på grynings­gryningen",
          {
            subtitle: "En pjäs jag gjorde igår - regisserad av Jörgen Sjögren",
            year: 2025,
            durationSeconds: 8400,
          }
        ),
      ]),
    ],
    /*
     * The menu, with something behind each entry.
     *
     * 🚨 A page here MUST carry rails. These used to be bare
     * `{subPageId, title}` pairs, which meant a preview could draw the tab bar
     * and prove nothing about what opening a tab does — and "the tab opens
     * onto nothing" is precisely the bug this surface exists to avoid.
     *
     * Deliberately unlike home: different titles, different counts, and one
     * page with a single short rail, because a menu whose pages all look like
     * the home screen tests nothing.
     */
    subPages: [
      {
        subPageId: "sub-kids",
        title: "Barn",
        icon: "child-care",
        rails: [
          rail("car-kids-new", "Nytt för barn", [
            item("k-1", "Rymdräven", { year: 2025, durationSeconds: 3600 }),
            item("k-2", "Bakom berget", { year: 2024, durationSeconds: 2700 }),
            item("k-3", "Pipsa och pumpan", {
              year: 2023,
              kind: "series",
              subtitle: "Säsong 1",
            }),
          ]),
          rail("car-kids-classics", "Klassiker", [
            item("k-4", "Skogens hjärta", { imageUrl: undefined, year: 1998 }),
            item("k-5", "Den långa vintern", {
              imageUrl: undefined,
              year: 2001,
            }),
          ]),
        ],
      },
      {
        subPageId: "sub-news",
        title: "Nyheter",
        icon: null,
        // One rail, and a short one: the narrowest page a menu can open onto
        // while still being worth opening.
        rails: [
          rail("car-news-today", "I dag", [
            item("n-1", "Morgonstudion", { year: 2026 }),
          ]),
        ],
      },
    ],
  };
}
